import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';
import { denormalize, type Schema } from 'normalizr';

import { entitySchemas } from './schemas';
import type {
  EntitiesMap,
  EntitiesState,
  PartialEntities,
  StoreEntityKey,
} from './types';

const createSelectPartialEntities = (relevantKeys: StoreEntityKey[]) => {
  return createSelector(
    relevantKeys.map((key) => (state: EntitiesState) => state[key]),
    (...entitiesParts) => {
      const result: PartialEntities = {};
      entitiesParts.forEach((entitiesPart, index) => {
        const relevantKey = relevantKeys[index];
        // Couldn't find a way to type this properly, main problem is
        // that there's a union of types assigned to a same union of types,
        // but using a key, which triggers TS to check if all entities are cross-compatible.
        // Which we know they aren't.
        // biome-ignore lint/plugin: see the comment above, a union of entity maps is assigned by key so TypeScript demands cross-compatibility the entities do not have.
        result[relevantKey] = entitiesPart as any;
      });
      return result;
    },
  );
};

const getSchemaKeys = (schema: Schema) => {
  // Asserting with any to get dependent keys of schema, they seem not exposed in types.
  // biome-ignore lint/plugin: normalizr does not expose the dependent keys of a schema in its types.
  const schemaAny = schema as any;
  // biome-ignore lint/plugin: the keys come from normalizr schema internals, which are untyped.
  const result = Object.keys(schemaAny.schema).flatMap((key) => {
    const schemaElement = schemaAny.schema[key];
    return Array.isArray(schemaElement)
      ? schemaElement.map((item: any) => item._key)
      : schemaElement._key;
  }) as StoreEntityKey[];
  return result;
};

export const createEntitiesDenormalizer = <EntityType extends StoreEntityKey>(
  entityType: EntityType,
) => {
  const schema = entitySchemas[entityType];
  const relevantKeys = getSchemaKeys(schema).concat(entityType);

  // Per-instance cache of the previous denormalized objects, keyed by id.
  // Reuses a prior object reference when an entity's value is unchanged, so
  // we can skip re-rendering unchanged items sometimes.
  let previousById = new Map<string, EntitiesMap[EntityType]>();
  let previousResult: EntitiesMap[EntityType][] | undefined;

  return createSelector(
    createSelectPartialEntities(relevantKeys),
    (_state: EntitiesState, ids: string[]) => ids,
    (entities, ids): EntitiesMap[EntityType][] => {
      // biome-ignore lint/plugin: denormalize() returns a loose record keyed by schema name.
      const freshEntities = denormalize(
        { [entityType]: ids },
        { [entityType]: [schema] },
        entities,
      )[entityType] as EntitiesMap[EntityType][];

      const nextById = new Map<string, EntitiesMap[EntityType]>();
      const reconciledEntities = freshEntities.map((entity) => {
        if (!entity || !('id' in entity) || !entity.id) {
          return entity;
        }
        const id = entity.id;
        const previous = previousById.get(id);
        const stable =
          previous && isEqual(previous, entity) ? previous : entity;
        nextById.set(id, stable);
        return stable;
      });
      previousById = nextById;

      // Keep the previous array reference when nothing inside changed, so
      // list consumers (memoized components, useSelector) don't re-render
      // on unrelated entity updates.
      if (
        previousResult &&
        previousResult.length === reconciledEntities.length &&
        reconciledEntities.every(
          (entity, index) => previousResult?.[index] === entity,
        )
      ) {
        return previousResult;
      }
      previousResult = reconciledEntities;
      return reconciledEntities;
    },
  );
};

export type EntityDenormalizer<EntityType extends StoreEntityKey> = (
  state: EntitiesState,
  id: string,
) => EntitiesMap[EntityType] | undefined;

export const createEntityDenormalizer = <EntityType extends StoreEntityKey>(
  entityType: EntityType,
): EntityDenormalizer<EntityType> => {
  const schema = entitySchemas[entityType];
  const relevantKeys = getSchemaKeys(schema).concat(entityType);

  // Reuses the prior denormalized object when its value is unchanged, so
  // consumers subscribed via useSelector don't re-render on unrelated
  // entity updates.
  let previous: EntitiesMap[EntityType] | undefined;

  return createSelector(
    createSelectPartialEntities(relevantKeys),
    (_state: EntitiesState, id: string) => id,
    (entities, id): EntitiesMap[EntityType] | undefined => {
      const fresh = denormalize(
        { [entityType]: [id] },
        { [entityType]: [schema] },
        entities,
      )[entityType][0];
      if (previous !== undefined && isEqual(previous, fresh)) {
        return previous;
      }
      previous = fresh;
      return fresh;
    },
  );
};
